import type { AloftPrice, SensitivityFeatureKey } from '@aloft/toolkit-shared';
import { SENSITIVITY_ITERATION_ORDER } from '@aloft/toolkit-shared';

import type { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';

export type AdjSalePriceLineItem = {
  label: string;
  adjustment: AloftPrice;
};

/**
 * Short, diagnostic-modal-style labels for each sensitivity feature, matching the existing
 * convention used by RegressionModalFeatureTable (e.g. "Parking" rather than "Garage /
 * Carport", "GLA" rather than "Gross Living Area") and the Figma spec for this breakdown
 * (Adjustment Support, node 2253:69645).
 */
const SENSITIVITY_FEATURE_SHORT_LABELS: Record<SensitivityFeatureKey, string> = {
  gla: 'GLA',
  garageCarport: 'Parking',
  fullBaths: 'Bath (Full)',
  halfBaths: 'Bath (Half)',
  bedrooms: 'Bed',
  quality: 'Quality',
  condition: 'Condition',
  siteSize: 'Site Size',
};

/** Reads a single sensitivity feature's current manual adjustment amount off a comp's CompAdjustments. */
export const getSensitivityFeatureAdjustment = (
  compAdjustment: CompAdjustments | undefined,
  feature: SensitivityFeatureKey,
): AloftPrice | undefined => {
  switch (feature) {
    case 'gla':
      return compAdjustment?.mainDwelling.gla.adjustment;
    case 'garageCarport':
      return compAdjustment?.garageCarport.adjustment;
    case 'fullBaths':
      return compAdjustment?.fullBaths.adjustment;
    case 'halfBaths':
      return compAdjustment?.halfBaths.adjustment;
    case 'bedrooms':
      return compAdjustment?.bedrooms.adjustment;
    case 'quality':
      return compAdjustment?.quality.adjustment;
    case 'condition':
      return compAdjustment?.condition.adjustment;
    case 'siteSize':
      return compAdjustment?.site.size.adjustment;
    default:
      return undefined;
  }
};

/**
 * Builds the line items for the Sensitivity Analysis Modal's "Adj. Sale Price" breakdown
 * tooltip: the comp's known Date of Sale adjustment, PLUS every sensitivity-relevant
 * physical-feature adjustment the appraiser already has entered (GLA, Parking, baths, bed,
 * quality, condition, site size) — not just whichever Variable is currently selected in the
 * modal. These are the same per-feature adjustment amounts shown in the main Adjustments Grid
 * (`selectCompAdjustments`), so this tooltip no longer shows a narrower "market conditions
 * only" picture of what's baked into the comp's price.
 *
 * Concessions/Terms of Sale are intentionally excluded: both are `@deprecated` throughout the
 * data model (`ManualAdjustments`, `MarketConditionsAdjustments`, `CompAdjustments`), with no
 * replacement concept yet, so new code shouldn't add call sites for them.
 *
 * Only non-zero amounts are included (matches the Figma spec, which hides zero rows).
 */
export const getAdjSalePriceBreakdownLineItems = (
  compAdjustment?: CompAdjustments,
): AdjSalePriceLineItem[] => {
  const items: AdjSalePriceLineItem[] = [];

  const dateOfSaleAdjustment = compAdjustment?.dateOfSale.adjustment;
  if (dateOfSaleAdjustment && dateOfSaleAdjustment.unitAmount !== 0) {
    items.push({ label: 'Date of Sale', adjustment: dateOfSaleAdjustment });
  }

  SENSITIVITY_ITERATION_ORDER.forEach((feature) => {
    const adjustment = getSensitivityFeatureAdjustment(compAdjustment, feature);
    if (adjustment && adjustment.unitAmount !== 0) {
      items.push({ label: SENSITIVITY_FEATURE_SHORT_LABELS[feature], adjustment });
    }
  });

  return items;
};
